import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import { pulse } from '../net/useDevice.js'

/**
 * How long the phone may sit untouched before the pending card asks again.
 * Long enough that it is not pestering somebody mid-decision, short enough
 * that a phone put down face-up gets picked back up in the same round.
 */
const IDLE_MS = 12_000
/**
 * A beat before the first buzz of a turn - which is now all a turn opens with.
 * A cinematic that just finished is still fading off the screen, and the card
 * underneath it has only just laid out.
 */
const SETTLE_MS = 400
/** The shake, and then the card is still again. */
const SHAKE_MS = 520
/**
 * How long the card sits still between twitches - and before the first of
 * them, because the turn itself no longer opens with one. A card that twitched
 * once and then sat perfectly still was easy to miss and easy to mistake for
 * something already dealt with; a tic that keeps coming back reads as an
 * unanswered question. But it is a reminder, so it waits: nothing moves at the
 * moment the card arrives, and what follows is spaced far enough apart to read
 * as the game waiting rather than as the screen fidgeting.
 */
const SHAKE_EVERY_MS = 9000
/** Headroom the toast needs over the card before it will sit above it. */
const TOAST_ROOM = 58
/** How often the toast looks again at where the card it is labelling has got to. */
const TRACK_MS = 250

/**
 * Two short buzzes rather than one long one. The single 180ms pulse this
 * replaces read as a notification from somewhere else on the phone; a double
 * tap reads as this app, and it is the same pattern every time it repeats.
 */
const PATTERN = [30, 70, 30]

/** A registered card, and whether it may do more than be labelled. */
interface Anchor {
  el: HTMLElement
  /** Buzz and shake. False on the screens the game means to be argued over. */
  loud: boolean
}

interface NudgeApi {
  /**
   * The card the table is waiting on registers itself here and gets back its
   * unmount callback. Only one is ever mounted at a time - the phone shows one
   * phase - so the last registration wins.
   */
  register: (el: HTMLElement, loud: boolean) => () => void
  /** True for the half-second of each twitch. */
  shaking: boolean
}

const NudgeContext = createContext<NudgeApi | null>(null)

/**
 * What a phone does when the table is waiting on you: a line naming what the
 * game wants, up for as long as it wants it; the card holding the control
 * twitching every few seconds; and, less often, a haptic pulse.
 *
 * This replaced a full-bleed "YOUR TURN" screen. That screen had the problem
 * every interstitial has - it covered the board you need in order to decide,
 * and it was spent the moment it appeared. A phone put down on the table with
 * a ballot open stalled the whole game and said nothing more about it. So the
 * alert became something that points at the pending control instead of
 * covering it, and something that does not stop until the thing is done.
 *
 * Three speeds, because one would have to be wrong. The toast is simply true
 * the whole time and costs a strip of screen. The shake is small - the thing
 * you catch out of the corner of an eye - and it keeps its distance: nothing
 * twitches at the moment the turn lands, and the tic that follows is slow
 * enough to be read as waiting rather than as nagging. The buzz is the loud
 * version and stays rare, or it stops being loud.
 *
 * Nothing here is allowed to cross the screen. A ring travelling out of the
 * pending control read as an event rather than a reminder, and at this
 * cadence - every few seconds, for as long as it takes somebody to make up
 * their mind - an event that size is something you fight rather than notice.
 *
 * Two of the three are held back on the screens the game means to be argued
 * over. Nominating a Chancellor and pointing a power at somebody are the
 * moments the table lobbies the President out loud, and a phone buzzing and
 * twitching through that is arguing back. The line still says what is
 * outstanding, because that is true there as much as anywhere.
 *
 * Resetting the two clocks on any input is what makes the repeat bearable. A
 * player turning the phone over, scrolling to the board, or halfway through a
 * press-and-hold is not stuck, and a card that shook itself out from under a
 * thumb mid-hold would be actively worse than silence.
 */
export function NudgeProvider({
  active,
  anchored = false,
  signature,
  label,
  children,
}: {
  /** The table is waiting on this phone and nothing else is on screen. */
  active: boolean
  /**
   * The pending control is docked to the viewport and names itself - see
   * ActionSheet - so the floating line stands down and only the twitch and the
   * buzz are left.
   *
   * The toast exists because the card could be scrolled away from and had to
   * be pointed at from wherever it had got to. A card that cannot leave the
   * screen does not need pointing at, and a sheet whose handle already reads
   * "Your turn - cast your ballot" with the same line hovering above it is
   * just the sentence twice.
   */
  anchored?: boolean
  /** Changes when the pending action does; that is what re-arms the alert. */
  signature: string
  /** What the game is asking for, in four words. */
  label: string
  children: ReactNode
}) {
  const [anchor, setAnchor] = useState<Anchor | null>(null)
  const [shaking, setShaking] = useState(false)
  /** Where the top of the card is right now, which is what the toast follows. */
  const [cardTop, setCardTop] = useState<number | null>(null)
  /** Bumped by every buzz, which is the other thing that restarts the clocks. */
  const [pulses, setPulses] = useState(0)
  /** Bumped by anything the player does, which is what restarts the wait. */
  const [touched, setTouched] = useState(0)

  /** There is a pending card on screen, so it gets named. */
  const armed = active && anchor !== null
  /** ...unless something else on screen is already naming it. */
  const labelled = armed && !anchored
  /** And on most of them, asked for. */
  const loud = armed && anchor.loud
  const el = anchor?.el ?? null

  /**
   * The loud part: a buzz, and - on every one of them but the turn's first - a
   * twitch to go with it. The opening buzz goes out bare, because a card that
   * shakes in the same moment it appears reads as the screen stumbling rather
   * than as the game asking; the tic has its own clock and will get to it.
   */
  const fire = useCallback((twitch: boolean) => {
    setPulses((n) => n + 1)
    if (twitch) setShaking(true)
    pulse(PATTERN)
  }, [])

  /*
   * The toast stays up for as long as the game is waiting, so it has to follow
   * the card rather than being placed once and left. A phone scrolls between
   * the controls and the board constantly, and a line pinned to where the card
   * used to be ends up over something else or off the screen entirely.
   *
   * Scroll is the one worth listening for, because it is the one that has to
   * look smooth. Everything else - an action error appearing above the card, a
   * veil opening and the card growing, the hand coming back a tile shorter -
   * is caught by simply looking again a few times a second. A ResizeObserver
   * is the obvious tool and the wrong one: the card in all of those cases has
   * not changed size, it has been pushed, and an observer watching a box it
   * still fits never fires.
   */
  useEffect(() => {
    if (!labelled || !el) {
      setCardTop(null)
      return
    }
    let frame = 0
    const measure = () => {
      frame = 0
      const top = el.getBoundingClientRect().top
      // Only when it has actually moved. This runs four times a second and a
      // re-render per tick would restart nothing but would cost everything.
      setCardTop((current) => (current !== null && Math.abs(current - top) < 1 ? current : top))
    }
    const schedule = () => {
      if (frame === 0) frame = window.requestAnimationFrame(measure)
    }
    measure()
    const opts = { capture: true, passive: true } as const
    window.addEventListener('scroll', schedule, opts)
    window.addEventListener('resize', schedule)
    const poll = window.setInterval(schedule, TRACK_MS)
    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule, opts)
      window.removeEventListener('resize', schedule)
      window.clearInterval(poll)
    }
  }, [labelled, el])

  // Any contact at all is the player being present. Capture phase, because a
  // press that lands on a button still counts and the button may well stop the
  // event before it reaches the window.
  useEffect(() => {
    if (!armed) return
    const bump = () => setTouched((n) => n + 1)
    const onVisible = () => {
      if (document.visibilityState === 'visible') bump()
    }
    const opts = { capture: true, passive: true } as const
    window.addEventListener('pointerdown', bump, opts)
    window.addEventListener('keydown', bump, opts)
    window.addEventListener('touchstart', bump, opts)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('pointerdown', bump, opts)
      window.removeEventListener('keydown', bump, opts)
      window.removeEventListener('touchstart', bump, opts)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [armed])

  // The first nudge of a turn. Keyed on the signature so a re-render never
  // re-fires it and a new phase always does.
  const fired = useRef<string | null>(null)
  useEffect(() => {
    if (!loud) {
      fired.current = null
      return
    }
    if (fired.current === signature) return
    fired.current = signature
    const timer = window.setTimeout(() => fire(false), SETTLE_MS)
    return () => window.clearTimeout(timer)
  }, [loud, signature, fire])

  // And every repeat after it. Restarted by the nudge itself as well as by the
  // player, so the gap is measured from whichever happened last.
  useEffect(() => {
    if (!loud) return
    const timer = window.setTimeout(() => {
      if (document.visibilityState !== 'visible') return
      fire(true)
    }, IDLE_MS)
    return () => window.clearTimeout(timer)
  }, [loud, touched, pulses, fire])

  /*
   * And the tic in between - which is also the turn's first twitch, now that
   * the opening buzz goes out on its own. The buzz is the loud version and
   * stays rare; this is just the thing saying it is still waiting, far enough
   * apart to be noticed out of the corner of an eye rather than fidgeted at.
   *
   * Restarted by the player and by the buzz both: a phone somebody is actually
   * using is not stuck, and a card that has just twitched alongside a pulse
   * does not need to twitch again half a second later.
   *
   * Raised whenever the table is waiting at all, where the buzz is held back
   * on the screens the game means to be argued over. Who acts on it is the
   * reader's business: `usePendingCard` passes it to the card only on the
   * screens that may be twitched at, and the folded action sheet - which is a
   * bar with four words on it and no other way of saying it is still waiting -
   * takes it everywhere. That is the same bargain the line above the card
   * always had on those screens: saying what is outstanding costs nobody their
   * turn to talk; interrupting them does.
   */
  useEffect(() => {
    if (!armed) return
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return
      setShaking(true)
    }, SHAKE_EVERY_MS)
    return () => window.clearInterval(timer)
  }, [armed, touched, pulses])

  useEffect(() => {
    if (!shaking) return
    const timer = window.setTimeout(() => setShaking(false), SHAKE_MS)
    return () => window.clearTimeout(timer)
  }, [shaking])

  // Nothing is left shaking on a card that has gone, or on one that has fallen
  // quiet because the phase changed under it.
  useEffect(() => {
    if (armed) return
    setShaking(false)
  }, [armed])

  const register = useCallback((node: HTMLElement, isLoud: boolean) => {
    setAnchor({ el: node, loud: isLoud })
    return () => setAnchor((current) => (current?.el === node ? null : current))
  }, [])

  const api = useMemo(() => ({ register, shaking }), [register, shaking])

  return (
    <NudgeContext.Provider value={api}>
      {children}
      {labelled && cardTop !== null && label && (
        /*
         * Above the card when there is room over it, and down at the thumb
         * when there is not. A card scrolled so its top is off screen is
         * filling the viewport, and a line clamped to the top of that lands
         * squarely on the card's own title - which is the one thing on screen
         * already saying what the game wants.
         *
         * Never re-keyed: it is up for the whole wait, not for a beat after
         * each nudge, so re-mounting it would replay the entrance animation
         * every few seconds at something already sitting there.
         */
        <p
          className={`nudge-toast ${cardTop < TOAST_ROOM ? 'nudge-toast--low' : ''}`}
          role="status"
          data-testid="nudge-toast"
          style={cardTop < TOAST_ROOM ? undefined : { top: `${cardTop - 46}px` }}
        >
          <span className="nudge-toast__kicker">Your turn</span>
          <span className="nudge-toast__label">{label}</span>
        </p>
      )}
    </NudgeContext.Provider>
  )
}

/**
 * The tic on its own, for something that is not the pending card.
 *
 * The card's own twitch is invisible the moment the action sheet is folded
 * down - the card is still mounted and still registered, but it is behind a
 * `display: none` - and a folded sheet is exactly when a reminder is worth
 * most. So the bar it folds to bobs on the same clock instead: one signal,
 * one cadence, whichever of the two is actually on screen.
 *
 * False outside a provider, and false on the phases the game means to be
 * argued over, which never raise it.
 */
export function useNudgeShake(): boolean {
  return useContext(NudgeContext)?.shaking ?? false
}

/**
 * Claims the gold frame every card the table is waiting on already wears, and
 * - unless the phase is one the game expects to be talked through - registers
 * the card as the thing the nudge points at.
 *
 * Null outside a provider, so the phases still render bare in Storybook.
 */
export function usePendingCard(
  /** Wear the gold frame and be named: the table is waiting on you for this card. */
  turn: boolean,
  /**
   * And be asked for out loud - buzzed and twitched at. False on the two
   * screens where the game expects the table to argue at you first: nominating
   * a Chancellor, and choosing who to point a power at. Being buzzed at while
   * nine people are still lobbying you is nagging rather than helping.
   *
   * It does not silence the line above the card. That the table is waiting on
   * you is as true there as anywhere, and saying so costs nobody their turn to
   * talk - what those screens cannot have is something that keeps interrupting.
   */
  nudge = true,
): { ref: (el: HTMLElement | null) => void; className: string } {
  const api = useContext(NudgeContext)
  // A callback ref held in state rather than a ref object, because a component
  // can carry one of these across several branches that each render a
  // different card - Executive does - and attaching has to be what starts the
  // registration, not the render that happened to run first.
  const [el, setEl] = useState<HTMLElement | null>(null)
  const register = api?.register

  useEffect(() => {
    if (!turn || !register || !el) return
    return register(el, nudge)
  }, [turn, nudge, register, el])

  const shaking = turn && nudge && Boolean(api?.shaking)
  return {
    ref: setEl,
    className: `${turn ? 'card--turn' : ''} ${shaking ? 'is-nudged' : ''}`.trim(),
  }
}
